import {
	compositionIDSuffixByPlacement,
	QualityTVPlacements,
	qtvProvidersByType,
} from '@/features/buybox/helpers/quality-tv-data';
import { getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useSimilarTitles } from '@/helpers/composables/useSimilarTitles';

import { SnowplowContext, SnowplowImpressionContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { FromRefOrGetter, WithRequired } from '@/helpers/types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { type MaybeRefOrGetter, toValue } from 'vue';
import { computed, ref } from 'vue';
import { PopularTitlesSorting, TitleFilter } from '@/@types/graphql-types';
import { useUserAgent } from '@/helpers/composables/useUserAgent';
import { GetQtvPopularTitlesDocument, GetQtvPopularTitlesQuery } from '@/pages/graphql/queries/GetPopularTitles.query';
import { captureMessageForSentry } from '@/libraries/sentry';
import { useBatchImpressionTracking } from '@/helpers/composables/useBatchImpressionTracking';
import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { OfferLocation } from '@/helpers/offer-helper';
import {
	getCompositionIdPrefix,
	updateCTAAffiliation,
	updatePosterAffiliation,
} from '@/features/buybox/helpers/qtv-promotion-helper';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useAdServer } from '@/features/ad-server/composables/useAdServer';
import { useGridSwiperExp } from '@/components/experiments/GridSwiperExp';
import { usePreRollExp } from '@/components/experiments/PreRollAndStaticBBAdsExp';
import useUnderPerformingUrlsExp from '@/components/experiments/UnderPerformingUrlsExp';

export interface UseQTVOptions {
	shortName: MaybeRefOrGetter<string>;
	titlesShownCount: MaybeRefOrGetter<number>;
	sourceTitle: MaybeRefOrGetter<TitleDetail>;
	additionalContexts?: MaybeRefOrGetter<SnowplowContext[]>;
	placement?: QualityTVPlacements;
	seasonNumber?: MaybeRefOrGetter<number | null | undefined>;
	episodeNumber?: MaybeRefOrGetter<number | null | undefined>;
	titleCount?: number;
	swiperIndex?: MaybeRefOrGetter<number | undefined>;
	// EXP_JWTV_TVOD | EXP_AVOD_AVOD
	providerFilter?: MaybeRefOrGetter<TitleFilter | undefined>;
}

export type SortQualityTv = 'imdb' | 'none';

export type QTVSimilarTitle = FromRefOrGetter<FromRefOrGetter<ReturnType<typeof useQTV>>['qtvTitles']>[number];

type SimilarityScores = {
	min: number | null;
	max: number | null;
	avg: number | null;
};

/**
 * This composable retrieves Quality TV titles and manages tracking and affiliation logic.
 * It supports two versions of the QualityTV component, which have the same functionality but different UIs.
 */
export function useQTV({
	shortName,
	titlesShownCount,
	sourceTitle,
	additionalContexts = [],
	placement = 'TitlePage',
	seasonNumber = null,
	episodeNumber = null,
	titleCount = 8,
	swiperIndex,
	providerFilter,
}: UseQTVOptions) {
	type SimilarTitle = ReturnType<typeof useQTVSimilarTitles>['qtvTitles']['value'][number];
	type QTVTitle = WithRequired<SimilarTitle, 'offers'>;

	const { country } = storeToRefs(useLanguageStore());
	const { xServeTrack } = storeToRefs(useAppStore());
	const { adServerIds } = useAdServer(() => toValue(sourceTitle).id);

	const { placement: clickoutPlacement } = getClickoutTrackingPlacement('qtv');
	const { getQTVCompositionId } = useQTVCompositionId();

	const _shortName = computed(() => toValue(shortName));
	const _seasonNumber = computed(() => toValue(seasonNumber));
	const _episodeNumber = computed(() => toValue(episodeNumber));
	const { promotionOffer: fallbackPromotionOffer } = usePromotion({
		title: sourceTitle,
		offers: () => [],
		hardcodedShortName: shortName,
	});

	const title = computed(() => {
		const _sourceTitle = toValue(sourceTitle);

		return _sourceTitle.__typename === 'Season' ? _sourceTitle.show! : _sourceTitle;
	});

	const _addtionalContexts = computed(() => toValue(additionalContexts) ?? []);

	const { setGridSwiperTags } = useGridSwiperExp();
	const { setPreRollTags } = usePreRollExp();
	const { setUnderPerformingUrlsExpTags } = useUnderPerformingUrlsExp();

	/** DATA FETCHING */
	const { qtvTitles, loading, isQTVEmpty, similarityScores } = useQTVSimilarTitles({
		title: sourceTitle,
		shortName,
		titleCount,
		providerFilter: providerFilter,
	});

	/** Clickout URLs */
	/**
	 * memoizedClickoutUrls - This ensures the clickoutUrl class is only instantiated once per title,
	 * this class stores the interlinking uuids, so this is important to persist.
	 */
	const isFreeTrialQTV = computed(() => qtvProvidersByType.FREE.includes(_shortName.value));
	const memoizedClickoutUrls = computed(() => {
		const fallbackOffer = isFreeTrialQTV.value ? fallbackPromotionOffer.value : qtvTitles.value[0]?.offers[0];

		return {
			qtvTitleUrls: qtvTitles.value.map((title): ClickoutUrl => new ClickoutUrl(title.offers[0])),
			ctaUrl: new ClickoutUrl(fallbackOffer),
		};
	});

	/**
	 * applies all clickout properties, contexts and affiliation rules for the clickout URLs.
	 */
	const clickoutUrls = computed<ClickoutUrl[]>(() =>
		memoizedClickoutUrls.value.qtvTitleUrls.map(
			(url: ClickoutUrl, index: number): ClickoutUrl =>
				url
					.apply(u => posterClickoutUrlUrlProperties(u, qtvTitles.value[index]))
					.apply(u =>
						updatePosterAffiliation({
							url: u,
							qtvTitle: qtvTitles.value[index],
							shortName: _shortName.value,
							country: country.value,
							getCompositionId,
						})
					)
					.apply(url => setGridSwiperTags(url, 'qtv', _shortName.value))
					.apply(url => setPreRollTags(url, 'qtv', _shortName.value))
					.apply(u => setUnderPerformingUrlsExpTags(u, 'qtv', _shortName.value))
		)
	);

	/**
	 * applies all clickout properties, contexts and affiliation rules for the CTA Url.
	 */
	const ctaClickoutUrl = computed(() =>
		memoizedClickoutUrls.value.ctaUrl.apply(ctaClickoutUrlProperties).apply(url =>
			updateCTAAffiliation({
				url: url,
				shortName: _shortName.value,
				country: country.value,
				compositionId: compositionIdFromHash.value,
			})
				.apply(url => setGridSwiperTags(url, 'qtv', _shortName.value))
				.apply(url => setPreRollTags(url, 'qtv', _shortName.value))
				.apply(u => setUnderPerformingUrlsExpTags(u, 'qtv', _shortName.value))
		)
	);

	function baseClickoutUrlProperties(u: ClickoutUrl) {
		return u
			.addContexts(impressionContext.value)
			.addContexts(_addtionalContexts.value)
			.setForTracking('offerLocation', OfferLocation.FREE_TRIAL)
			.set('uct_country', country.value)
			.updateContext('ClickoutContextGraphql', 'placement', clickoutPlacement)
			.updateContext('ClickoutContextGraphql', 'swiperIndex', toValue(swiperIndex))
			.setCanary(xServeTrack.value)
			.setAdIds(adServerIds.value);
	}

	function posterClickoutUrlUrlProperties(u: ClickoutUrl, qtvTitle: QTVTitle) {
		const rawScore = similarityScores.value[qtvTitle.id] ?? null;
		return u
			.apply(baseClickoutUrlProperties)
			.addContexts(getTitleContext(qtvTitle))
			.addContexts(getRecommenderContext(getCompositionId(qtvTitle.id), rawScore))
			.setTitleSourceId(qtvTitle.id)
			.updateContext('ClickoutContextGraphql', 'clickoutType', 'qtv');
	}

	function ctaClickoutUrlProperties(u: ClickoutUrl) {
		return u
			.apply(baseClickoutUrlProperties)
			.addContexts(getRecommenderContext(compositionIdFromHash.value))
			.updateContext('ClickoutContextGraphql', 'clickoutType', 'qtv_cta')
			.addContexts(ctaTitleContext.value);
	}

	/** TRACKING */

	/**  Tracks the visibility state of each title
	 * Store visible titles in a Set to avoid duplicates, and leverages array reactivity for composition ID updates
	 */
	const visibleTitleIdsSet = ref(new Set<string>());
	const visibleTitleIds = ref<string[]>([]);

	const impressionContext = computed(() => {
		const { sortedIds, indexOffset } = getSortedIds(qtvTitles.value, visibleTitleIds.value);

		return new SnowplowImpressionContext(visibleTitleIds.value.length, indexOffset, sortedIds);
	});

	/**
	 * Extract aggregated similarityScores from titles that have them (from similarTitlesV2)
	 * Returns null for popular titles fallback
	 */
	const aggregatedScores = computed<SimilarityScores>((): SimilarityScores => {
		const scores = Object.values(similarityScores.value);

		if (scores.length === 0) {
			return { min: null, max: null, avg: null };
		}

		return {
			min: Math.min(...scores),
			max: Math.max(...scores),
			avg: scores.reduce((sum, score) => sum + score, 0) / scores.length,
		};
	});

	const recommenderContext = computed(() => getRecommenderContext(compositionIdFromHash.value));

	const ctaTitleContext = computed(() =>
		isFreeTrialQTV.value
			? new SnowplowTitleContextGraphql(
					title.value.objectId,
					title.value.objectType,
					_seasonNumber.value,
					_episodeNumber.value
			  )
			: getTitleContext(qtvTitles.value[0])
	);

	/** compositionIdFromHash - Used to generate a unique composition ID of the visible titles IDs
	 * This is included in the impression events, as well as the affiliation of ATV+ clickout URLs.
	 */
	const compositionIdFromHash = computed(() => {
		const { sortedIds } = getSortedIds(qtvTitles.value, visibleTitleIds.value);
		const hashID = generateHashFromIDs(sortedIds);
		return getCompositionId(hashID);
	});

	const intersectionVariables = computed(() => ({
		additionalContexts: () => [recommenderContext.value],
		additionalProperties: () => ({ value: Math.min(toValue(titlesShownCount), qtvTitles.value.length) }),
		preventDuplicateAdditionalContext: true,
	}));

	/** Tracking for recommender_feature */
	const { onIntersection } = useBatchImpressionTracking<QTVTitle>({
		category: 'recommender_feature',
		toContext: getTitleContext,
		...intersectionVariables.value,
	});

	/** Tracking for posters and CTA */
	const additionalContextsWithAdServerContexts = computed(() => {
		// pass all additional contexts including ad server contexts
		return (intersectionVariables.value.additionalContexts() as SnowplowContext[]).concat(_addtionalContexts.value);
	});

	const { onIntersection: onIntersectionOfferImpression } = useOfferImpressionTracking({
		additionalContexts: additionalContextsWithAdServerContexts.value,
		additionalProperties: intersectionVariables.value.additionalProperties,
		groupId: 'qtv',
	});

	const { onIntersection: onIntersectionOfferImpressionCTA } = useOfferImpressionTracking({
		additionalContexts: additionalContextsWithAdServerContexts.value,
		additionalProperties: intersectionVariables.value.additionalProperties,
		groupId: 'qtv_cta',
	});

	function onVisibilityChanged(title: QTVTitle, index: number, isVisible: boolean) {
		handleVisibleTitlesChange(isVisible, title.id);

		if (!isVisible) return;

		setTimeout(() => {
			onIntersection(title, isVisible);
			onIntersectionOfferImpression(clickoutUrls.value[index], isVisible, [getTitleContext(title)]);
			// Delay impression firing until compositionId is up-to-date
			// All impressions need to fire befor the recommenderContext is correct
			// Without the setTimeout, the recommenderContext is not reliably accurate
			// Would prefer this to be a more explicit solution, but this is the best we can do for now
		}, 200);
	}

	function onCTAVisibilityChanged(isVisible: boolean) {
		setTimeout(() => {
			onIntersectionOfferImpressionCTA(ctaClickoutUrl.value, isVisible, [ctaTitleContext.value]);
		}, 200);
	}

	/** handleVisibleTitlesChange
	 * This function updates the visible titles set and the reactive array.
	 * This is used to generate the composition ID with hash of visible titles.
	 * Logic is separated where possible, to limit the reactivite changes to the composition ID
	 * Changes to the composition ID will trigger a re-render of the component, as the cx param on the ClickoutAnchor href updates.
	 * This causes render loops if not handled correctly.
	 */
	function handleVisibleTitlesChange(isVisible: boolean, id: string) {
		const wasVisible = visibleTitleIdsSet.value.has(id);

		// avoid unnecessary state updates triggering componenent re-renders
		if (isVisible === wasVisible) return;

		if (isVisible && !wasVisible) visibleTitleIdsSet.value.add(id);
		if (!isVisible && wasVisible) visibleTitleIdsSet.value.delete(id);

		// Sync the reactive array and the derived composition ID
		visibleTitleIds.value = Array.from(visibleTitleIdsSet.value);
	}

	/** HELPER FUNCTIONS */
	function getCompositionId(id: string) {
		const sourceTitleID = title.value.id ?? 'nsid';
		const suffix = placement === 'TitlePage' ? null : compositionIDSuffixByPlacement[placement];
		const prefix = getCompositionIdPrefix(_shortName.value);
		return getQTVCompositionId(prefix, sourceTitleID, id, suffix);
	}

	function getRecommenderContext(compositionID: string | null = null, rawScore: number | null = null) {
		return TrackingHelper.getRecommenderContext(
			'quality_tv',
			title.value.id,
			_seasonNumber.value,
			null,
			compositionID,
			aggregatedScores.value.min,
			aggregatedScores.value.max,
			aggregatedScores.value.avg,
			rawScore
		);
	}

	function getTitleContext(qtvTitle: QTVTitle) {
		const index = qtvTitles.value.findIndex(title => title.id === qtvTitle.id);
		const indexForTitleContext = index > -1 ? index + 1 : undefined;

		return new SnowplowTitleContextGraphql(
			qtvTitle.objectId,
			qtvTitle.objectType,
			undefined,
			null,
			undefined,
			indexForTitleContext
		);
	}

	return {
		// data
		qtvTitles,
		loading,
		isQTVEmpty,
		// tracking
		onVisibilityChanged,
		onCTAVisibilityChanged,
		// clickout URLs
		ctaClickoutUrl,
		clickoutUrls,
	};
}

function useQTVSimilarTitles({
	title,
	shortName,
	titleCount,
	providerFilter,
}: {
	title: MaybeRefOrGetter<TitleDetail>;
	shortName: MaybeRefOrGetter<string>;
	titleCount: number;
	// EXP_JWTV_TVOD | EXP_AVOD_AVOD
	providerFilter?: MaybeRefOrGetter<TitleFilter | undefined>;
}) {
	const _titleId = computed(() => {
		const _sourceTitle = toValue(title);

		return _sourceTitle.__typename === 'Season' ? _sourceTitle.show.id : _sourceTitle.id;
	});

	const _shortName = computed(() => toValue(shortName));
	const _providerFilter = computed(() => toValue(providerFilter) ?? {});
	const {
		similarTitles,
		similarityScores,
		loading: similarTitlesLoading,
	} = useSimilarTitles({
		titleId: _titleId,
		filters: () => ({
			imdbScore: { min: 7.5 },
			packages: [_shortName.value],
			..._providerFilter.value,
		}),
		offerFilters: () => ({ packages: [_shortName.value] }),
		includeOffers: true,
		first: titleCount,
		minTitles: titleCount,
	});

	const fetchPopularTitles = computed(() => !similarTitlesLoading.value && similarTitles.value.length < titleCount);

	const { popularTitles, loading: popularTitlesLoading } = usePopularTitles({
		shortName,
		first: titleCount,
		enabled: fetchPopularTitles,
		providerFilter: _providerFilter.value,
	});

	const loading = computed(() => similarTitlesLoading.value || popularTitlesLoading.value);

	const qtvTitles = computed(() => {
		if (loading.value) return [];

		const titles = fetchPopularTitles.value ? popularTitles.value : similarTitles.value;
		const _titles = titles.filter(title => (title.offers?.length ?? 0) > 0);

		return _titles.sort((a, b) => (b.content.scoring.imdbScore ?? 0) - (a.content.scoring.imdbScore ?? 0));
	});

	const isQTVEmpty = computed(() => qtvTitles.value.length === 0);

	const _similarityScores = computed(() => {
		if (fetchPopularTitles.value) return {};
		return similarityScores.value;
	});

	return { qtvTitles, loading, isQTVEmpty, similarityScores: _similarityScores };
}

/** If similarTitlesV2 returns empty, we enable this popularTitles query and fetch 8 high quality popular titles */
function usePopularTitles({
	shortName,
	first,
	enabled,
	providerFilter,
}: {
	shortName: MaybeRefOrGetter<string>;
	first: MaybeRefOrGetter<number>;
	enabled: MaybeRefOrGetter<boolean>;
	providerFilter?: MaybeRefOrGetter<TitleFilter>;
}) {
	const { country, language } = storeToRefs(useLanguageStore());
	const { result, loading, onError } = useQuery<GetQtvPopularTitlesQuery>(
		GetQtvPopularTitlesDocument,
		() => ({
			country: country.value,
			popularTitlesSortBy: PopularTitlesSorting.Popular_7Days,
			first: toValue(first),
			popularTitlesFilter: { packages: [toValue(shortName)], imdbScore: { min: 8 }, ...toValue(providerFilter) },
			language: language.value,
			offerFilters: {
				packages: [toValue(shortName)],
				preAffiliate: true,
			},
		}),
		() => ({ enabled: toValue(enabled), errorPolicy: 'all' })
	);

	const popularTitles = computed(() => result.value?.popularTitles?.edges?.map(({ node }) => node) ?? []);

	onError(error => {
		captureMessageForSentry(
			`[usePopularTitles - useQTV] Error fetching popular titles`,
			{ error, packages: [toValue(shortName)], first: toValue(first) },
			'error'
		);
	});

	return {
		popularTitles,
		loading,
	};
}

/** useQTVCompositionId - Aligns QTV compositionID for all QTV types - QTV, SQTV */
export function useQTVCompositionId() {
	const { deviceOs, deviceType } = useUserAgent();
	const deviceOsParam = computed(() => {
		return ['ios', 'macos'].includes(deviceOs.value) ? 'os' : deviceOs.value === 'android' ? 'and' : 'ot';
	});

	const deviceTypeParam = computed(() => (deviceType.value === 'mobile' ? 'mo' : 'cm'));
	const deviceParam = computed(() => `${deviceTypeParam.value}-${deviceOsParam.value}`);

	function getQTVCompositionId(
		prefix: string,
		titleId: string,
		/** For SQTV and QTV CTA, we pass the similar title id, else a hash of the similar titles */
		similarTitleIdOrHash: string,
		suffix?: string | null
	) {
		return [prefix, titleId, similarTitleIdOrHash, deviceParam.value, suffix].filter(Boolean).join('-');
	}

	return {
		getQTVCompositionId,
		deviceParam,
	};
}

function getSortedIds(qtvTitles: { id: string }[], visibleIds: string[]) {
	const sortedIds = visibleIds
		.slice()
		.filter(id => qtvTitles.some(title => title.id === id))
		.sort((a, b) => {
			const indexOfA = qtvTitles.findIndex(t => t.id === a);
			const indexOfB = qtvTitles.findIndex(t => t.id === b);
			return indexOfA - indexOfB;
		});
	const indexOffset = sortedIds.length > 0 ? qtvTitles.findIndex(t => t.id === sortedIds[0]) : 0;

	return { sortedIds, indexOffset };
}

function simpleHash(str: string) {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = (hash << 5) - hash + str.charCodeAt(i);
		hash |= 0; // Convert to 32bit integer
	}
	return Math.abs(hash).toString(36); // Convert to base36 string
}

function generateHashFromIDs(idList: string[]) {
	const idString = idList.join('-');
	let hash = simpleHash(idString);
	// Ensure the resulting string is exactly 6 characters long
	if (hash.length < 6) {
		// If shorter than 6, pad with 0s
		hash = hash.padEnd(6, '0');
	} else if (hash.length > 6) {
		// If longer than 6, trim to 6 characters
		hash = hash.slice(0, 6);
	}
	return hash;
}
